import React, { useMemo } from 'react';
import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { prompt } from '@dashboard/lib/prompt';
import { apiUrl } from '@dashboard/services/env';
import request from '@dashboard/services/request';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import PasswordForm from './components/PasswordForm';
import NameForm from './components/NameForm';
import EmailForm from './components/EmailForm';
import ConnectedAccountsForm from './components/ConnectedAccountsForm';
import AppearanceForm from './components/AppearanceForm';
import { toast } from '@/dashboard/context/ToastContext';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';

const AccountProfile = () => {
  const { token, logout, user, availableStores } = useAuth();

  const hasGoogleAccount =
    user?.googleId && (user?.authProvider === 'google' || user?.authProvider === 'both');

  const ownedStores = useMemo(
    () => availableStores?.filter(s => s.owner === user?._id) || [],
    [availableStores, user?._id],
  );
  const ownsStores = ownedStores.length > 0;

  async function handleDeleteAccount() {
    const confirmationDeleteWord = 'DELETE';
    const result = await prompt(
      `Are you sure? Your account and all associated data will be permanently deleted. Type "${confirmationDeleteWord}" to confirm.`,
      {
        confirm: 'Yes, delete my account',
        cancelText: 'Cancel',
        validate: value => {
          if (value !== confirmationDeleteWord) {
            return 'The text you entered does not match. Please try again.';
          }
        },
      },
    );

    if (result) {
      const deleted = await request(`${apiUrl}/delete`, token);

      if (deleted !== true) {
        toast({
          type: 'error',
          title: 'Unable to delete your account.',
          description: deleted?.message || 'Please contact support.',
          duration: 6000,
        });
        return;
      }

      logout();
    }
  }

  return (
    <SettingsContainer>
      <SettingsHeader id="name">Change name</SettingsHeader>
      <NameForm />

      <SettingsDivider />

      <SettingsHeader id="email">Change email</SettingsHeader>
      <EmailForm />

      <SettingsDivider />

      <SettingsHeader id="password">Change password</SettingsHeader>
      <PasswordForm />

      <SettingsDivider />

      <SettingsHeader id="appearance">Appearance</SettingsHeader>
      <AppearanceForm />

      <SettingsDivider />

      {hasGoogleAccount && (
        <>
          <SettingsHeader id="connected-accounts">Connected accounts</SettingsHeader>
          <ConnectedAccountsForm />

          <SettingsDivider />
        </>
      )}

      <SettingsHeader id="delete">Delete account</SettingsHeader>

      {ownsStores && (
        <Alert variant="warning" className="my-2">
          <ExclamationCircleIcon />
          <AlertTitle className="line-clamp-none">
            Delete or transfer ownership of your stores first.
          </AlertTitle>
          <AlertDescription>
            <p>
              To delete your account, first delete or transfer ownership of all your stores in each{' '}
              <Link to="/settings/general" className="text-inherit underline">
                store's settings.
              </Link>
            </p>
          </AlertDescription>
        </Alert>
      )}

      <Button disabled={ownsStores} handleClick={handleDeleteAccount} appearance="light">
        Delete your account
      </Button>

      <SettingsDivider />

      <div>
        <Link href="/terms-of-service" variant="secondary" blank className="text-sm">
          Terms of service
        </Link>
      </div>
    </SettingsContainer>
  );
};

export default AccountProfile;
